import {
  Box,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  Option,
  Tooltip,
} from "@mui/joy";
import { FilterListIcon } from "../../../../styles/StyledMUI";
import DarkSelect from "components/common/DarkSelect";
import IconButtonCTA from "components/common/IconButtonCTA";
import type { UserWithRole } from "api/auth/Auth.typed";
import {
  colorTokens,
  hrFilterSelectSx,
  hrTooltipSurfaceSx,
  profileFormFieldLabelSx,
  textTokens,
  withAlpha,
} from "styles/globalStyles";
import { userDisplayName } from "utils/userHelper";
import type { PlanningCalendarScope } from "utils/planningLeaveHelpers";

export type { PlanningCalendarScope };

export type PlanningCategoryFilter = "all" | "leaves" | "bookings";

const PLANNING_SCOPE_LABELS: Record<PlanningCalendarScope, string> = {
  all: "Tout le monde",
  company: "Ma société",
  me: "Moi",
};

const PLANNING_CATEGORY_LABELS: Record<PlanningCategoryFilter, string> = {
  all: "Congés & Prestations",
  leaves: "Congés uniquement",
  bookings: "Prestations uniquement",
};

export function allowedPlanningCalendarScopes(perms: {
  hasPlanningAllView: boolean;
  hasPlanningCompanyView: boolean;
  hasPlanningMeView: boolean;
}): PlanningCalendarScope[] {
  if (perms.hasPlanningAllView) {
    return ["all", "company", "me"];
  }

  if (perms.hasPlanningCompanyView) {
    return ["company", "me"];
  }

  if (perms.hasPlanningMeView) {
    return ["me"];
  }

  return [];
}

export function coalescePlanningCalendarScope(
  allowed: PlanningCalendarScope[],
  current: PlanningCalendarScope,
): PlanningCalendarScope {
  if (allowed.length === 0) {
    return "all";
  }

  if (allowed.includes(current)) {
    return current;
  }

  if (allowed.includes("all")) {
    return "all";
  }

  if (allowed.includes("company")) {
    return "company";
  }

  return "me";
}

function defaultPlanningScopeForIndicator(allowed: PlanningCalendarScope[]): PlanningCalendarScope {
  if (allowed.includes("all")) {
    return "all";
  }

  if (allowed.includes("company")) {
    return "company";
  }

  return allowed[0] ?? "all";
}

export type PlanningFilterMenuProps = {
  employees: UserWithRole[];
  selectedUserId: number;
  onUserChange: (userId: number) => void;
  allowedPlanningScopes: PlanningCalendarScope[];
  planningScope: PlanningCalendarScope;
  onPlanningScopeChange: (scope: PlanningCalendarScope) => void;
  showEmployeeFilter: boolean;
  categoryFilter: PlanningCategoryFilter;
  onCategoryFilterChange: (category: PlanningCategoryFilter) => void;
};

const PLANNING_CATEGORY_OPTIONS: PlanningCategoryFilter[] = ["all", "leaves", "bookings"];

export function PlanningFilterMenu({
  employees,
  selectedUserId,
  onUserChange,
  allowedPlanningScopes,
  planningScope,
  onPlanningScopeChange,
  showEmployeeFilter,
  categoryFilter,
  onCategoryFilterChange,
}: PlanningFilterMenuProps) {
  const showScopeFilter = allowedPlanningScopes.length > 1;
  const defaultScope = defaultPlanningScopeForIndicator(allowedPlanningScopes);
  const scopeFilterActive = showScopeFilter && planningScope !== defaultScope;
  const userFilterActive = selectedUserId > 0;
  const categoryFilterActive = categoryFilter !== "all";
  const hasActiveFilter = scopeFilterActive || userFilterActive || categoryFilterActive;

  return (
    <Dropdown>
      <Tooltip title="Filtres">
        <MenuButton
          slots={{ root: IconButtonCTA }}
          slotProps={{
            root: {
              size: "sm",
              "aria-label": "Ouvrir le menu des filtres",
              ctaVariant: "accent",
            },
          }}
          sx={{ border: "none" }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <FilterListIcon sx={{ color: textTokens.color.primary }} />
            {hasActiveFilter ? (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: colorTokens.status.success,
                  boxShadow: `0 0 0 2px ${colorTokens.bg.surface}`,
                }}
              />
            ) : null}
          </Box>
        </MenuButton>
      </Tooltip>
      <Menu
        placement="bottom-end"
        sx={{
          ...hrTooltipSurfaceSx,
          minWidth: 280,
          maxWidth: "min(95vw, 380px)",
          overflow: "visible",
          p: 0,
          zIndex: 1300,
        }}
      >
        <Box
          sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          {showScopeFilter ? (
            <FormControl>
              <FormLabel sx={{ ...profileFormFieldLabelSx, mb: 0.5 }}>Périmètre</FormLabel>
              <DarkSelect
                aria-label="Périmètre du planning"
                placeholder="Périmètre"
                value={planningScope}
                onChange={(_, v) => {
                  if (v === null || v === undefined) {
                    return;
                  }

                  onPlanningScopeChange(String(v) as PlanningCalendarScope);
                }}
                sx={{ ...hrFilterSelectSx, color: textTokens.color.primary }}
                slotProps={{
                  listbox: {
                    disablePortal: true,
                  },
                }}
              >
                {allowedPlanningScopes.map((s) => (
                  <Option key={s} value={s}>
                    {PLANNING_SCOPE_LABELS[s]}
                  </Option>
                ))}
              </DarkSelect>
            </FormControl>
          ) : null}
          {showScopeFilter ? (
            <Divider sx={{ borderColor: withAlpha(textTokens.color.primary, 0.12), my: 0.25 }} />
          ) : null}
          <FormControl>
            <FormLabel sx={{ ...profileFormFieldLabelSx, mb: 0.5 }}>Types</FormLabel>
            <DarkSelect
              aria-label="Types"
              placeholder="Types"
              value={categoryFilter}
              onChange={(_, v) => {
                if (v === null || v === undefined) {
                  return;
                }

                onCategoryFilterChange(String(v) as PlanningCategoryFilter);
              }}
              sx={{ ...hrFilterSelectSx, color: textTokens.color.primary }}
              slotProps={{
                listbox: {
                  disablePortal: true,
                },
              }}
            >
              {PLANNING_CATEGORY_OPTIONS.map((c) => (
                <Option key={c} value={c}>
                  {PLANNING_CATEGORY_LABELS[c]}
                </Option>
              ))}
            </DarkSelect>
          </FormControl>
          {showEmployeeFilter ? (
            <>
              <Divider sx={{ borderColor: withAlpha(textTokens.color.primary, 0.12), my: 0.25 }} />
              <FormControl>
                <FormLabel sx={{ ...profileFormFieldLabelSx, mb: 0.25, opacity: 0.85 }}>
                  Utilisateur
                </FormLabel>
                <DarkSelect
                  aria-label="Utilisateur"
                  placeholder={
                    showScopeFilter ? "Tous les salariés du périmètre" : "Tous les salariés"
                  }
                  value={selectedUserId}
                  onChange={(_, value) =>
                    onUserChange(value === null || value === undefined ? -1 : Number(value))
                  }
                  sx={{ ...hrFilterSelectSx, color: textTokens.color.primary }}
                  slotProps={{
                    listbox: {
                      disablePortal: true,
                    },
                  }}
                >
                  <Option value={-1}>Tous</Option>
                  {employees.map((u) => (
                    <Option key={u.user.id} value={u.user.id}>
                      {userDisplayName(u.user)}
                    </Option>
                  ))}
                </DarkSelect>
              </FormControl>
            </>
          ) : null}
        </Box>
      </Menu>
    </Dropdown>
  );
}
